import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Smartphone, CreditCard, ShieldCheck, Clock, Zap } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, StatusBadge, Spinner, VerifiedChip } from "../components/common";
import { useAuth } from "../context/AuthContext";

const ICONS = { stripe: CreditCard, banking: Building2, moncash: Smartphone };
const BRAND = {
  banking: { bg: "rgba(75,35,204,0.12)", fg: "#6B3BF5" },
  moncash: { bg: "rgba(225,29,42,0.12)", fg: "#F0414E" },
};
const SUBTITLE = {
  banking: "Link a US bank for ACH transfers",
  moncash: "Top up from MonCash in Haiti",
};

export default function AddMoney() {
  const { user } = useAuth();
  const verified = !!(user?.identity_verified || (user?.badge_active && user?.badge_color === "blue"));
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feePct, setFeePct] = useState(0);
  const [mode, setMode] = useState(null);
  const [acctName, setAcctName] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, f] = await Promise.all([api.get("/deposit/methods"), api.get("/deposit/fees")]);
        setMethods(m.data); setFeePct(f.data.deposit_fee_percent); setMode(f.data.mode); setAcctName(f.data.account_name);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const stripe = methods.find((m) => m.key === "stripe" && m.connected);
  const amt = parseFloat(amount) || 0;
  const fee = amt * feePct / 100;
  const credited = amt - fee;

  const payWithStripe = async () => {
    if (amt <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { amount: amt, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const tapOther = (m) => toast(`${m.name} is coming soon.`, { description: "We'll let you know the moment it's ready." });

  if (loading) return <FullLoader />;

  const otherMethods = methods.filter((m) => !(m.key === "stripe" && m.connected));

  return (
    <div className="animate-fade-up">
      <PageHeader title="Add Money" subtitle="Top up your Jack Cash balance" />

      {verified && <div className="mb-4"><VerifiedChip verified testid="addmoney-verified-chip" /></div>}

      {mode && (
        <div data-testid="stripe-mode-banner" className={`flex items-center gap-2 rounded-xl px-3 py-2 mb-4 text-xs font-semibold ${mode === "live" ? "bg-[#2F80FF]/10 text-[#2F80FF]" : "bg-warning/10 text-warning"}`}>
          <CreditCard size={14} />
          <span className="flex-1">
            {acctName ? <>Paying to <span className="font-bold">{acctName}</span> · </> : null}
            {mode === "live" ? "Live payments — real money" : "Test mode — no real money is charged"}
          </span>
        </div>
      )}

      {stripe && (
        <div className="rounded-3xl bg-card border border-border p-5 mb-5 shadow-[0_10px_40px_-18px_rgba(75,35,204,0.5)]">
          <div className="flex items-center gap-2.5 mb-1">
            <span className="grid place-items-center w-10 h-10 rounded-2xl bg-primary/10 text-primary"><Zap size={18} /></span>
            <div className="flex-1">
              <div className="font-display font-semibold leading-tight">Pay instantly</div>
              <p className="text-xs text-muted-foreground">Card, Apple Pay or Google Pay</p>
            </div>
            <StatusBadge label="CONNECTED" />
          </div>
          <div className="flex flex-wrap gap-2 my-4">
            {["Card", "Apple Pay", "Google Pay"].map((m) => (
              <span key={m} data-testid={`wallet-pill-${m.toLowerCase().replace(/\s/g, "-")}`}
                className="px-3 py-1.5 rounded-full bg-secondary text-xs font-semibold text-foreground/80">{m}</span>
            ))}
          </div>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-3xl font-light text-muted-foreground">$</span>
            <input data-testid="deposit-amount-input" type="number" step="0.01" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
              className="w-full h-16 pl-10 pr-4 rounded-2xl bg-secondary border border-transparent focus:border-primary focus:bg-card outline-none amount-figure text-3xl" />
          </div>
          <div className="flex gap-2 mt-3">
            {[25, 50, 100, 200].map((v) => (
              <button key={v} data-testid={`deposit-preset-${v}`} onClick={() => setAmount(String(v))}
                className={`flex-1 h-10 rounded-xl text-sm font-semibold transition-colors ${String(v) === amount ? "bg-primary text-primary-foreground" : "bg-secondary text-primary"}`}>${v}</button>
            ))}
          </div>
          {amt > 0 && (
            <div className="rounded-2xl bg-secondary/60 p-4 mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">You pay</span><span className="font-medium">${amt.toFixed(2)}</span></div>
              {feePct > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Fee ({feePct}%)</span><span className="font-medium">-${fee.toFixed(2)}</span></div>}
              <div className="h-px bg-border" />
              <div className="flex justify-between"><span className="font-semibold">Added to balance</span><span className="font-semibold">${credited.toFixed(2)}</span></div>
            </div>
          )}
          <button data-testid="deposit-pay-btn" onClick={payWithStripe} disabled={busy || amt <= 0}
            className="w-full h-14 mt-4 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center shadow-[0_12px_32px_rgba(75,35,204,0.3)] disabled:opacity-50">
            {busy ? <Spinner className="w-5 h-5" /> : "Continue to payment"}
          </button>
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground text-center mt-3">
            <ShieldCheck size={13} className="text-success" /> Secured by Stripe · balance updates only after payment confirms
          </p>
        </div>
      )}

      {/* Other methods — coming soon */}
      {otherMethods.length > 0 && (
        <div>
          <div className="flex items-center gap-3 px-1 mb-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">More ways to add money</span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <div className="space-y-2.5">
            {otherMethods.map((m) => {
              const Icon = ICONS[m.key] || Building2;
              const tint = BRAND[m.key] || { bg: "var(--secondary)", fg: "var(--primary)" };
              return (
                <button key={m.key} data-testid={`deposit-method-${m.key}`} onClick={() => tapOther(m)}
                  className="group w-full flex items-center gap-4 p-4 rounded-2xl bg-card border border-border text-left transition-all duration-200 hover:border-primary/30 active:scale-[0.99]">
                  <span className="grid place-items-center w-12 h-12 rounded-2xl shrink-0" style={{ background: tint.bg, color: tint.fg }}><Icon size={22} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[15px]">{m.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{SUBTITLE[m.key] || "Coming soon"}</div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground bg-secondary px-2.5 py-1 rounded-full shrink-0">
                    <Clock size={11} /> Soon
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
